// Client-side validation for the 'regionalHeatingFactors' admin setting
// (AHA-6796). Mirrors the server-side zod refinement (@alpinehomeair/common
// RegionalHeatingFactorsSchema, used by system-selector-api
// regional-heating-factors-setting.ts), so a payload
// that passes here always passes the server parse: every factor finite and
// > 0; fallback required, finite, > 0; at least one bucket; exactly one
// bucket unbounded above (maxTempF === null) and exactly one unbounded below
// (minTempF === null); bounded rows need minTempF < maxTempF; sorted buckets
// contiguous (adjacent maxTempF === next minTempF) with no gaps or overlaps.
// An empty result object means the form is valid.
export type RegionalHeatingFactorRowDraft = {
  minTempF: number | null; // null = blank input = unbounded below
  maxTempF: number | null; // null = blank input = unbounded above
  factor: number | undefined;
};

export type RegionalHeatingFactorRowErrors = {
  minTempF?: string;
  maxTempF?: string;
  factor?: string;
};

export interface RegionalHeatingFactorsErrors {
  rows?: Record<number, RegionalHeatingFactorRowErrors>;
  fallbackFactor?: string;
  form?: string[];
}

export function validateRegionalHeatingFactors(
  rows: RegionalHeatingFactorRowDraft[],
  fallbackFactor: number | undefined
): RegionalHeatingFactorsErrors {
  const errors: RegionalHeatingFactorsErrors = {};
  const rowErrors: Record<number, RegionalHeatingFactorRowErrors> = {};
  const formErrors: string[] = [];

  rows.forEach((row, index) => {
    const error: RegionalHeatingFactorRowErrors = {};
    if (row.factor === undefined || !Number.isFinite(row.factor)) {
      error.factor = 'Enter a number';
    } else if (row.factor <= 0) {
      error.factor = 'Must be greater than 0';
    }
    if (
      row.minTempF !== null &&
      row.maxTempF !== null &&
      row.minTempF >= row.maxTempF
    ) {
      error.maxTempF = 'Must be greater than the min temp';
    }
    if (Object.keys(error).length > 0) {
      rowErrors[index] = error;
    }
  });

  if (fallbackFactor === undefined || !Number.isFinite(fallbackFactor)) {
    errors.fallbackFactor = 'Enter a number';
  } else if (fallbackFactor <= 0) {
    errors.fallbackFactor = 'Must be greater than 0';
  }

  if (rows.length === 0) {
    formErrors.push('At least one bucket is required');
  } else {
    const unboundedAbove = rows.filter((row) => row.maxTempF === null).length;
    if (unboundedAbove !== 1) {
      formErrors.push(
        'Exactly one bucket must have a blank max temp (no upper limit)'
      );
    }
    const unboundedBelow = rows.filter((row) => row.minTempF === null).length;
    if (unboundedBelow !== 1) {
      formErrors.push(
        'Exactly one bucket must have a blank min temp (no lower limit)'
      );
    }
    // Contiguity/overlap: sorted descending by effective min (blank min
    // sorts last), each bucket's max must meet the previous bucket's min
    // exactly — the same rule as the server-side zod refinement.
    const sorted = [...rows].sort(
      (a, b) => (b.minTempF ?? -Infinity) - (a.minTempF ?? -Infinity)
    );
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].maxTempF !== sorted[i - 1].minTempF) {
        formErrors.push(
          "Buckets must be contiguous and non-overlapping (each bucket's max temp must equal the next-warmer bucket's min temp)"
        );
        break;
      }
    }
  }

  if (Object.keys(rowErrors).length > 0) {
    errors.rows = rowErrors;
  }
  if (formErrors.length > 0) {
    errors.form = formErrors;
  }
  return errors;
}
